import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { IGetCategoriesItemDto } from '../categories.dto';
import { IAdvanceFilter } from '../../shared/advanced-filters/components/advance-filter';
import { ContractControlAdvanceFilterParamType } from '../contract-control/advanced-filters';
import { DefaultFilter } from '../contract-control/contract-control-interfaces/default-filters.interface';
import { IContractControlData, IContractMonthlyCost, IContractMonthlyRevenue, ICustomBoxesDaum, IRevenueDTO } from '../contract-control/contract-control.dto';
import { IPeriodRenewal } from '../contract-control/store/dto/contract-control-view';
import { IDocumentTypesDto } from '../documents/dto/document-listing.dto';

export interface IContractDataUpdate {
  id: number;
  data: {
    contract_no?: string;
    categories?: IGetCategoriesItemDto[];
    group?: any;
    document_type?: IDocumentTypesDto;
    [key: string]: any;
  }
}

export interface IBoxesData {
  key: string;
  data: IRevenueDTO | Partial<IContractControlData> | Partial<ICustomBoxesDaum> | Partial<IContractMonthlyCost> | Partial<IContractMonthlyRevenue>;
  contract_id?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ContractControlEmitterService {

  private auditTrailEmitter = new EventEmitter<{fetch_logs: number}>();
  private editLayoutSettings = new EventEmitter<IDocumentTypesDto>();
  private upComingGraphEmitter = new EventEmitter<{reinit: number}>();
  private periodRenewalProgressBar = new EventEmitter<{data: { contract_id: number, period_renewal: IPeriodRenewal }}>();
  private defaultFiltersTrigger = new EventEmitter<DefaultFilter>();
  private advancedFiltersTrigger = new EventEmitter< IAdvanceFilter<ContractControlAdvanceFilterParamType>[]>();
  private ccBoxesData = new EventEmitter<IBoxesData>();
  private contractUpdated = new EventEmitter<void>();
  private updateContractDataEmitter = new EventEmitter<IContractDataUpdate>();

  constructor() { }


  /**
   * After creating audit log then emitting this emitter to patch data into emitter
   * @param data 
   */
  dispatchAuditTrail(data: {fetch_logs: number}){
    this.auditTrailEmitter.emit(data);
  }

  /**
   * Get audit trail emitter in audit modal in real time
   * @returns Audit trail Emitter
   */
  getAuditTrailEmitter(): Observable<{fetch_logs: number}>{
    return this.auditTrailEmitter.asObservable();
  }

  /**
   * Patch Edit Layout Setting To Emitter
   * @param layoutSettings 
   */
  dispatchEditLayoutSetting(layoutSettings: IDocumentTypesDto){
    this.editLayoutSettings.emit(layoutSettings);
  }

  /**
   * Get Edit Layout Setting Emitter
   * @returns Emitter
   */
  getEditLayoutSettingEmitter(): Observable<IDocumentTypesDto>{
    return this.editLayoutSettings.asObservable();
  }

  /**
   * Trigger upcoming graph
   * @param setting 
   */
  triggerUpComingGraph(setting: {reinit: number}){
    this.upComingGraphEmitter.emit(setting);
  }

  /**
   * Get upcoming graph trigger
   * @returns 
   */
  getUpComingGraphTrigger(): Observable<{reinit: number}>{
    return this.upComingGraphEmitter.asObservable();
  }


  /**
   * Trigger Progress bar on listing for period renewal circle
   * @param data 
   */
  triggerProgressBar(data: any){
    this.periodRenewalProgressBar.emit({data: data});
  }

  /**
   * Get Period renewal data
   * @returns 
   */
  getProgressBarTrigger(): Observable<{data: { contract_id: number, period_renewal: IPeriodRenewal }}>{
    return this.periodRenewalProgressBar.asObservable();
  }


  /**
   * Set default filters
   * @param data 
   */
  setDefaultFilters(data: DefaultFilter){
    this.defaultFiltersTrigger.emit(data);
  }

  /**
   * Set advanced filters
   * @param data
   */
  setAdvancedFilters(data: IAdvanceFilter<ContractControlAdvanceFilterParamType>[]){
    this.advancedFiltersTrigger.emit(data);
  }


  /**
   * Get default filters
   * @returns Observable
   */
  getDefaultFilters(): Observable<DefaultFilter>{
    return this.defaultFiltersTrigger.asObservable().pipe(distinctUntilChanged((prev, curr) => prev!=null && curr!=null && JSON.stringify(prev) === JSON.stringify(curr)));
  }

 /**
   * Get advanced filters
   * @returns Observable
   */
  getAdvancedFilters(): Observable<IAdvanceFilter<ContractControlAdvanceFilterParamType>[]>{
    return this.advancedFiltersTrigger.asObservable();
  }


  /**
   * Set Contract control boxes data for syncing in right sidebar
   * @param data 
   */
  setBoxesData(data: IBoxesData){
    this.ccBoxesData.emit(data);
  }


  /**
   * Get Contract control boxes data from syncing in sidebar
   * @returns 
   */
  getBoxesData(): Observable<IBoxesData>{
    return this.ccBoxesData.asObservable();    
  }

  /**
   * Trigger contract updated event
   */
  triggerContractUpdated(){
    this.contractUpdated.emit();
  }

  /**
   * Get contract updated event
   * @returns Observable
   */
  getContractUpdated(): Observable<void>{
    return this.contractUpdated.asObservable();
  }

  /**
   * Trigger generic contract data update
   * @param data
   */
  triggerUpdateContractData(data: IContractDataUpdate){
    this.updateContractDataEmitter.emit(data);
  }

  /**
   * Get generic contract data update
   * @returns Observable
   */
  getUpdateContractData(): Observable<IContractDataUpdate>{
    return this.updateContractDataEmitter.asObservable();
  }
}
